import ApiService from './apiService';
import { ISolexTokenRequest, ISolexTokenResponse } from '../models/ISolexTokenResponse';

/**
 * Service for integrating with Solex Document Editor
 * Handles OAuth token exchange and secure form POST submission
 */
export class SolexEditorService {
  private apiService: ApiService;
  private readonly TOKEN_EXCHANGE_ENDPOINT = '/solex/editor-session/token';

  constructor() {
    // Remove /concept from base URL for Solex endpoint (similar to PluginService pattern)
    const baseUrl = process.env.REACT_APP_EPC_API as string;
    const solexEndpoint = baseUrl.replace('/concept', '');
    this.apiService = ApiService.getInstance(solexEndpoint);
  }

  /**
   * Exchange Solex editor URL for session token via backend proxy (EN-3228)
   * @param solexEditorUrl - The Solex editor URL from DocGen response
   * @returns Promise resolving to ISolexTokenResponse with actionUrl and accessToken
   * @throws Error if URL is invalid, API call fails, or backend returns errors
   */
  async getSolexSessionToken(solexEditorUrl: string): Promise<ISolexTokenResponse> {
    try {
      // Validate input URL format
      if (!solexEditorUrl || solexEditorUrl.trim() === '') {
        throw new Error('Solex editor URL is required');
      }

      new URL(solexEditorUrl); // Throws if invalid URL format

      console.log('Requesting Solex session token from backend...');

      // Build request body
      const requestBody: ISolexTokenRequest = {
        url: solexEditorUrl
      };

      // Make POST API call - ApiService automatically injects Bearer token via interceptor
      const axiosResponse = await this.apiService.post<ISolexTokenResponse>(
        this.TOKEN_EXCHANGE_ENDPOINT,
        requestBody,
        {
          headers: {
            'Content-Type': 'application/json',
            'Authorization': 'Bearer' // Placeholder - ApiService interceptor will inject actual token
          }
        }
      );

      // Extract response data from AxiosResponse
      const response = axiosResponse.data;

      // Check for backend validation errors
      if (response.validationErrors && response.validationErrors.length > 0) {
        const errorMessage = response.validationErrors.join(', ');
        console.error('Token exchange validation errors:', response.validationErrors);
        throw new Error(`Validation errors: ${errorMessage}`);
      }

      // Check for backend exceptions
      if (response.exceptionMessage) {
        console.error('Token exchange exception:', response.exceptionMessage);
        throw new Error(`Backend exception: ${response.exceptionMessage}`);
      }

      // Verify response has required fields
      if (!response.result || !response.result.actionUrl || !response.result.accessToken) {
        throw new Error('Invalid response: missing actionUrl or accessToken');
      }

      console.log('Successfully received Solex session token');
      return response;
    } catch (error) {
      console.error('Failed to get Solex session token:', error);
      throw error;
    }
  }

  /**
   * Create and submit hidden form to open Solex editor in new window
   * Uses form POST pattern to securely send access token (not in URL)
   * @param actionUrl - The Solex editor action URL
   * @param accessToken - The session access token to submit
   * @param targetWindow - Optional Window reference opened earlier to avoid popup blockers
   * @returns boolean indicating success (true) or failure (false)
   */
  openSolexEditorWithForm(actionUrl: string, accessToken: string, targetWindow?: Window | null): boolean {
    let formAppended = false;
    let form: HTMLFormElement | null = null;

    try {
      // Validate inputs
      if (!actionUrl || actionUrl.trim() === '') {
        console.error('Action URL is required');
        return false;
      }

      if (!accessToken || accessToken.trim() === '') {
        console.error('Access token is required');
        return false;
      }

      // Validate action URL format and enforce HTTPS-only
      const parsedUrl = new URL(actionUrl);
      if (parsedUrl.protocol !== 'https:') {
        throw new Error(`Invalid URL scheme: ${parsedUrl.protocol}. Only HTTPS is allowed.`);
      }

      console.log('Creating form to open Solex Document Editor...');

      // Create hidden form element
      form = document.createElement('form');
      form.method = 'POST';
      form.action = actionUrl;

      // If a target window was provided, submit to it; otherwise open new window
      if (targetWindow && !targetWindow.closed) {
        // Use the pre-opened window to avoid popup blockers
        const windowName = 'solexEditor_' + Date.now();
        (targetWindow as any).name = windowName;
        form.target = windowName;
      } else {
        form.target = '_blank'; // Fallback to new window
      }

      form.style.display = 'none';

      // Create hidden input for access token
      const input = document.createElement('input');
      input.type = 'hidden';
      input.name = 'access_token';
      input.value = accessToken;

      // Assemble form
      form.appendChild(input);
      document.body.appendChild(form);
      formAppended = true;

      // Submit form
      form.submit();

      console.log('Form submitted successfully - Solex editor should open in new window');
      return true;
    } catch (error) {
      console.error('Failed to open Solex editor with form:', error);
      return false;
    } finally {
      // Always clean up form from DOM if it was appended (security: prevents token leakage)
      if (formAppended && form) {
        try {
          document.body.removeChild(form);
        } catch (cleanupError) {
          console.warn('Failed to remove form from DOM:', cleanupError);
        }
      }
    }
  }

  /**
   * High-level orchestration method to open Solex Document Editor
   * Combines token exchange and form submission
   * @param solexEditorUrl - The Solex editor URL from DocGen response
   * @param targetWindow - Optional Window reference opened earlier to avoid popup blockers
   * @returns Promise resolving to boolean indicating overall success
   */
  async openSolexEditor(solexEditorUrl: string, targetWindow?: Window | null): Promise<boolean> {
    try {
      console.log('Opening Solex Document Editor...');

      // Step 1: Exchange Okta token for Solex session token
      const tokenResponse = await this.getSolexSessionToken(solexEditorUrl);

      // Step 2: Submit form to open editor in new window (or pre-opened window)
      // getSolexSessionToken validates and throws if result/actionUrl/accessToken are missing,
      // so a defensive check here guards against unexpected runtime edge cases.
      if (!tokenResponse.result?.actionUrl || !tokenResponse.result?.accessToken) {
        throw new Error('Unexpected missing result data after token exchange');
      }

      const formSuccess = this.openSolexEditorWithForm(
        tokenResponse.result.actionUrl,
        tokenResponse.result.accessToken,
        targetWindow
      );

      if (formSuccess) {
        console.log('Solex Document Editor opened successfully');
        return true;
      } else {
        console.error('Form submission failed');
        return false;
      }
    } catch (error) {
      console.error('Failed to open Solex Document Editor:', error);
      return false;
    }
  }
}

// Internal singleton instance (lazy initialized, not exported to allow test mocking)
let solexEditorServiceInstance: SolexEditorService | null = null;

const getSolexEditorService = (): SolexEditorService => {
  if (!solexEditorServiceInstance) {
    solexEditorServiceInstance = new SolexEditorService();
  }
  return solexEditorServiceInstance;
};

// Export main function for easy import (delegates to internal singleton)
export const openSolexEditor = (url: string, targetWindow?: Window | null): Promise<boolean> =>
  getSolexEditorService().openSolexEditor(url, targetWindow);
